// ========================================
// Hook לשליפת היסטוריית פריט מ-Supabase
// ========================================
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook לשליפת היסטוריית מלאי ומכירות עבור פריט מסוים
 * @param {string} sku - מק"ט הפריט
 * @param {number} months - כמה חודשים אחורה לשלוף (ברירת מחדל: 3)
 */
export const useItemHistory = (sku, months = 3) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!sku) {
            setData([]);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // חישוב תאריך התחלה (X חודשים אחורה)
            const startDate = new Date();
            startDate.setMonth(startDate.getMonth() - months);
            const startDateStr = startDate.toISOString().split('T')[0];

            // שליפת Snapshots מ-Supabase
            // הערה: הטבלה snapshots מכילה עמודת data שהיא JSON array של כל הפריטים
            // נצטרך לסנן את הפריט הספציפי בצד הלקוח
            const { data: snapshots, error: dbError } = await supabase
                .from('snapshots')
                .select('snapshot_date, data')
                .gte('snapshot_date', startDateStr)
                .order('snapshot_date', { ascending: true });

            if (dbError) throw dbError;

            // עיבוד הנתונים - חילוץ הפריט הספציפי מכל snapshot
            const historyData = snapshots
                .map(snapshot => {
                    const items = snapshot.data || [];
                    const item = items.find(i => i.sku === sku);

                    if (!item) return null;

                    return {
                        date: snapshot.snapshot_date,
                        physicalStock: item.physical_stock || 0,
                        onOrder: item.on_order || 0,
                        velocity: item.avg_daily_sales ? item.avg_daily_sales * 30 : 0, // המרה ליומי
                        // מחושב: מלאי נטו
                        netStock: (item.physical_stock || 0) + (item.on_order || 0),
                    };
                })
                .filter(Boolean); // הסרת nulls

            setData(historyData);

        } catch (err) {
            console.error('Error fetching item history:', err);
            setError('שגיאה בטעינת היסטוריית הפריט');
        } finally {
            setIsLoading(false);
        }
    }, [sku, months]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchHistory,
        hasData: data.length > 0,
    };
};
